import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormikHelpers } from 'formik';

import EntityForm from '../../../../components/form/base/EntityForm';
import { ArticleValues } from '../../model/articleModel';
import TextField from '../../../../components/form/elements/TextField';
import {
  RADIO_COLOR,
  TEXT_FIELD_COLOR,
  TEXT_FIELD_TYPE,
  TEXT_FIELD_VARIANT,
} from '../../../../components/form/enum/formProps';
import * as CommonSelectors from '../../../../shared/redux/commonSelectors';
import * as CommonActions from '../../../../shared/redux/commonActions';
import MultiSelect from '../../../../components/form/elements/MultiSelect';
import Title from '../../../../components/Title';
import { BreadcrumbsLinkData } from '../../../../components/Breadcrumbs/model/breadcrumbsModel';
import Breadcrumbs from '../../../../components/Breadcrumbs';
import Radio from '../../../../components/form/elements/Radio';
import RadioGroup from '../../../../components/form/elements/RadioGroup';
import CategoryFilter from '../../../../components/form/custom/CategoryFilter';
import { API_REQUEST_STATE } from '../../../../core/enums/apiRequestState';
import Editor from '../../../../components/form/elements/Editor';
import { ENTITY_ACTIONS } from '../../../../components/form/base/EntityForm/enum/entityActions';

interface ArticleFormProps {
  categoryId?: number;
  title: string;
  breadcrumbs: BreadcrumbsLinkData[];
  initialValues: ArticleValues;
  onSubmit: (values: ArticleValues, formikHelpers?: FormikHelpers<ArticleValues>, actionType?: ENTITY_ACTIONS) => void;
  apiRequestState: API_REQUEST_STATE;
  isMainCategory?: boolean;
}

const validationSchema = Yup.object().shape({
  title: Yup.string().min(2, 'validation.too_short').required('validation.is_required'),
  content: Yup.string().min(2, 'validation.too_short').required('validation.is_required'),
  categoryOne: Yup.object().shape({
    value: Yup.string().ensure().required('validation.is_required'),
    key: Yup.string().ensure().required('validation.is_required'),
  }),
  categoryTwo: Yup.object().shape({
    value: Yup.string().ensure().required('validation.is_required'),
    key: Yup.string().ensure().required('validation.is_required'),
  }),
  categoryThree: Yup.object().shape({
    value: Yup.string().ensure().required('validation.is_required'),
    key: Yup.string().ensure().required('validation.is_required'),
  }),
  tags: Yup.array().ensure().required('validation.is_required'),
  keywords: Yup.array().ensure().required('validation.is_required'),
  isPublished: Yup.string().required('validation.is_required'),
  isNotificationNeeded: Yup.string().required('validation.is_required'),
});

const ArticleForm: React.FC<ArticleFormProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tagsList = useSelector(CommonSelectors.tagsOptionList);
  const keywordsList = useSelector(CommonSelectors.keywordsOptionList);

  useEffect(() => {
    dispatch(CommonActions.getTagsList());
  }, []);

  useEffect(() => {
    dispatch(CommonActions.getKeywordsList());
  }, []);

  return (
    <>
      <Title>{t(props.title)}</Title>
      <Breadcrumbs title={props.title} links={props.breadcrumbs} hide />
      <EntityForm<ArticleValues>
        initialValues={props.initialValues}
        validationSchema={validationSchema}
        onSubmit={props.onSubmit}
        enablePreview={true}
      >
        <TextField
          name="title"
          label="article.title"
          type={TEXT_FIELD_TYPE.TEXT}
          color={TEXT_FIELD_COLOR.PRIMARY}
          variant={TEXT_FIELD_VARIANT.STANDARD}
        />
        <CategoryFilter parentId={props.categoryId} readonly={props.isMainCategory} />
        <MultiSelect
          name="tags"
          label="article.tags"
          choices={tagsList}
          color={TEXT_FIELD_COLOR.PRIMARY}
          variant={TEXT_FIELD_VARIANT.STANDARD}
        />
        <MultiSelect
          name="keywords"
          label="article.keywords"
          choices={keywordsList}
          color={TEXT_FIELD_COLOR.PRIMARY}
          variant={TEXT_FIELD_VARIANT.STANDARD}
        />
        <RadioGroup label="article.is_published" name="isPublished">
          <Radio label="options.yes" value="true" color={RADIO_COLOR.PRIMARY} />
          <Radio label="options.no" value="false" color={RADIO_COLOR.PRIMARY} />
        </RadioGroup>
        <RadioGroup label="article.is_notification_needed" name="isNotificationNeeded">
          <Radio label="options.yes" value="true" color={RADIO_COLOR.PRIMARY} />
          <Radio label="options.no" value="false" color={RADIO_COLOR.PRIMARY} />
        </RadioGroup>
        <Editor name="content" label="article.content" apiRequestState={props.apiRequestState} />
      </EntityForm>
    </>
  );
};

export default ArticleForm;
